/**
 * Universal Session Recording SDK v3.0 - Optimized
 * rrweb-compatible session recording with performance-first implementation
 * Records user sessions with DOM snapshots, mutations, and user interactions
 * Sends data as type: "sessionrecord"
 *
 * Key optimizations (aligned with OptimizedSessionRecorder.tsx):
 * 1. Debounced API calls (sends data every 60s, not on every event)
 * 2. No localStorage abuse (minimal storage operations)
 * 3. No heavy JSON.stringify in hot paths
 * 4. Batch event processing with proper buffer management
 * 5. Proper cleanup and memory management
 * 6. Event restoration on API failure
 */

(function(window, document) {
  'use strict';

  // rrweb Event Types
  const EventType = {
    DomContentLoaded: 0,
    Load: 1,
    FullSnapshot: 2,
    IncrementalSnapshot: 3,
    Meta: 4,
    Custom: 5,
    Plugin: 6
  };

  // rrweb Incremental Source Types
  const IncrementalSource = {
    Mutation: 0,
    MouseMove: 1,
    MouseInteraction: 2,
    Scroll: 3,
    ViewportResize: 4,
    Input: 5,
    TouchMove: 6,
    MediaInteraction: 7,
    StyleSheetRule: 8,
    CanvasMutation: 9,
    Font: 10,
    Log: 11,
    Drag: 12,
    StyleDeclaration: 13,
    Selection: 14,
    AdoptedStyleSheet: 15
  };

  // Mouse Interaction Types
  const MouseInteractions = {
    MouseUp: 0,
    MouseDown: 1,
    Click: 2,
    ContextMenu: 3,
    DblClick: 4,
    Focus: 5,
    Blur: 6,
    TouchStart: 7,
    TouchMove_Departed: 8,
    TouchEnd: 9,
    TouchCancel: 10
  };

  // Media Interaction Types
  const MediaInteractions = {
    Play: 0,
    Pause: 1,
    Seeked: 2,
    VolumeChange: 3,
    RateChange: 4
  };

  // Configuration
  const CONFIG = {
    apiEndpoint: 'https://cvm-api.worktual.co.uk/cvm_api_service/v1/insert_tracking_nfspath',
    domainId: 20556,
    domainName: window.location.hostname,
    autoStart: true,
    maxEvents: 5000,
    mouseMoveThrottle: 50, // ms between mouse move events
    chunkInterval: 60000, // Send chunks every 60 seconds
    debug: false,

    // Privacy settings
    maskAllInputs: true,
    maskInputOptions: {
      password: true,
      email: true,
      tel: true,
      text: false,
      textarea: true
    },
    blockClass: 'sr-block',
    ignoreClass: 'sr-ignore',
    maskTextClass: 'sr-mask',
    maskAllText: false,
    inlineStylesheet: true,
    recordCanvas: false
  };

  // Session state - Optimized with proper lifecycle management
  const STATE = {
    visitorId: null,
    sessionId: null,
    isRecording: false,
    isMounted: true,  // Flag for safe cleanup (aligned with OptimizedSessionRecorder.tsx)
    startTime: 0,
    events: [],       // Event buffer - cleared immediately on send
    chunkNumber: 0,
    chunkTimer: null,
    nodeIdCounter: 1,
    nodeIdMap: new WeakMap(),
    idNodeMap: new Map(),
    lastMouseMoveTime: 0,
    mousePositions: [],
    observers: {
      mutation: null
    },
    scrollPositions: new Map(),
    pendingSend: false  // Prevent concurrent sends
  };

  // Utility: Generate unique ID
  function generateId(prefix) {
    return prefix + '_' + Date.now() + '_' + Math.random().toString(36).substring(2, 11);
  }

  // Utility: Get/Set Cookie
  function getCookie(name) {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    const cookie = cookies.find(function(row) { return row.startsWith(name + '='); });
    return cookie ? decodeURIComponent(cookie.split('=')[1]) : null;
  }

  function setCookie(name, value, days) {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; expires=' + expires + '; SameSite=Lax';
  }

  // Utility: Check consent
  function isConsentMissing() {
    const consent = getCookie('CK_Cons') || localStorage.getItem('cookieConsent');
    return !consent;
  }

  // Initialize Visitor ID
  function initVisitorId() {
    var visitorId = getCookie('visitor_id');
    if (!visitorId) {
      visitorId = generateId('visitor');
      setCookie('visitor_id', visitorId, 365);
    }
    STATE.visitorId = visitorId;
    return visitorId;
  }

  // Initialize Session ID
  function initSessionId() {
    var sessionId = sessionStorage.getItem('session_recording_id');
    if (!sessionId) {
      sessionId = generateId('session');
      sessionStorage.setItem('session_recording_id', sessionId);
    }
    STATE.sessionId = sessionId;
    return sessionId;
  }

  // Get or create node ID
  function getNodeId(node) {
    if (!node) return -1;

    var id = STATE.nodeIdMap.get(node);
    if (id === undefined) {
      id = STATE.nodeIdCounter++;
      STATE.nodeIdMap.set(node, id);
      STATE.idNodeMap.set(id, node);
    }
    return id;
  }

  // Serialize a DOM node for full snapshot
  function serializeNode(node, doc) {
    if (!node) return null;

    var id = getNodeId(node);

    // Document node
    if (node.nodeType === Node.DOCUMENT_NODE) {
      return {
        type: 0, // Document
        childNodes: serializeChildNodes(node, doc),
        id: id
      };
    }

    // DocumentType node
    if (node.nodeType === Node.DOCUMENT_TYPE_NODE) {
      return {
        type: 1, // DocumentType
        name: node.name || 'html',
        publicId: node.publicId || '',
        systemId: node.systemId || '',
        id: id
      };
    }

    // Element node
    if (node.nodeType === Node.ELEMENT_NODE) {
      var tagName = node.tagName.toLowerCase();

      // Skip blocked elements
      if (node.classList && node.classList.contains(CONFIG.blockClass)) {
        return {
          type: 2,
          tagName: tagName,
          attributes: {},
          childNodes: [],
          id: id
        };
      }

      // Skip script contents and certain elements
      if (tagName === 'script' || tagName === 'noscript') {
        return {
          type: 2,
          tagName: tagName,
          attributes: getAttributes(node),
          childNodes: [{
            type: 3,
            textContent: 'SCRIPT_PLACEHOLDER',
            id: getNodeId({ nodeType: Node.TEXT_NODE })
          }],
          id: id
        };
      }

      var serialized = {
        type: 2, // Element
        tagName: tagName,
        attributes: getAttributes(node),
        childNodes: serializeChildNodes(node, doc),
        id: id
      };

      // Handle SVG namespace
      if (node.namespaceURI && node.namespaceURI !== 'http://www.w3.org/1999/xhtml') {
        serialized.isSVG = true;
      }

      return serialized;
    }

    // Text node
    if (node.nodeType === Node.TEXT_NODE) {
      var textContent = node.textContent || '';

      // Mask text if needed
      if (CONFIG.maskAllText || (node.parentElement && node.parentElement.classList &&
          node.parentElement.classList.contains(CONFIG.maskTextClass))) {
        textContent = textContent.replace(/\S/g, '*');
      }

      return {
        type: 3, // Text
        textContent: textContent,
        id: id
      };
    }

    // Comment node
    if (node.nodeType === Node.COMMENT_NODE) {
      return {
        type: 4, // Comment
        textContent: node.textContent || '',
        id: id
      };
    }

    // CDATA node
    if (node.nodeType === Node.CDATA_SECTION_NODE) {
      return {
        type: 5, // CDATA
        textContent: node.textContent || '',
        id: id
      };
    }

    return null;
  }

  // Serialize child nodes
  function serializeChildNodes(node, doc) {
    var children = [];
    var child = node.firstChild;

    while (child) {
      var serialized = serializeNode(child, doc);
      if (serialized) {
        children.push(serialized);
      }
      child = child.nextSibling;
    }

    return children;
  }

  // Get element attributes
  function getAttributes(element) {
    var attrs = {};

    for (var i = 0; i < element.attributes.length; i++) {
      var attr = element.attributes[i];
      var name = attr.name;
      var value = attr.value;

      // Skip event handlers
      if (name.startsWith('on')) continue;

      // Mask input values
      if (name === 'value' && shouldMaskInput(element)) {
        value = '***';
      }

      attrs[name] = value;
    }

    // Capture inline styles as computed
    if (element.tagName.toLowerCase() === 'style' && CONFIG.inlineStylesheet) {
      try {
        var sheet = element.sheet;
        if (sheet && sheet.cssRules) {
          var cssText = '';
          for (var j = 0; j < sheet.cssRules.length; j++) {
            cssText += sheet.cssRules[j].cssText + ' ';
          }
          attrs._cssText = cssText.trim();
        }
      } catch (e) {
        // Cross-origin stylesheet
      }
    }

    // Capture link stylesheets as inline
    if (element.tagName.toLowerCase() === 'link' && element.rel === 'stylesheet' && CONFIG.inlineStylesheet) {
      try {
        var linkSheet = element.sheet;
        if (linkSheet && linkSheet.cssRules) {
          var linkCssText = '';
          for (var k = 0; k < linkSheet.cssRules.length; k++) {
            linkCssText += linkSheet.cssRules[k].cssText + ' ';
          }
          attrs._cssText = linkCssText.trim();
        }
      } catch (e) {
        // Cross-origin stylesheet - keep href
      }
    }

    // Handle checked state for inputs
    if (element.tagName.toLowerCase() === 'input') {
      if (element.type === 'checkbox' || element.type === 'radio') {
        attrs.checked = element.checked;
      }
    }

    // Handle selected for options
    if (element.tagName.toLowerCase() === 'option') {
      attrs.selected = element.selected;
    }

    return attrs;
  }

  // Check if input should be masked
  function shouldMaskInput(element) {
    if (!CONFIG.maskAllInputs) return false;

    var type = element.type ? element.type.toLowerCase() : 'text';
    var tagName = element.tagName.toLowerCase();

    if (tagName === 'textarea') {
      return CONFIG.maskInputOptions.textarea !== false;
    }

    if (tagName === 'input') {
      if (CONFIG.maskInputOptions[type] !== undefined) {
        return CONFIG.maskInputOptions[type];
      }
      return CONFIG.maskInputOptions.text !== false;
    }

    return false;
  }

  // Record an event - Optimized: Simply buffer events, no heavy operations
  function recordEvent(event) {
    // Check both isRecording and isMounted for safety
    if (!STATE.isRecording || !STATE.isMounted) return;

    // Simply buffer the event - no JSON.stringify or heavy operations here
    STATE.events.push(event);

    // Limit events array size - send chunk when buffer is full
    if (STATE.events.length > CONFIG.maxEvents) {
      sendChunk();
    }

    // Debug logging only when explicitly enabled (avoid console overhead)
    if (CONFIG.debug) {
      console.log('📝 Event buffered:', event.type, 'Buffer size:', STATE.events.length);
    }
  }

  // Create Meta event (type 4)
  function createMetaEvent() {
    return {
      type: EventType.Meta,
      data: {
        href: window.location.href,
        width: window.innerWidth,
        height: window.innerHeight
      },
      timestamp: Date.now()
    };
  }

  // Create Full Snapshot event (type 2)
  function createFullSnapshot() {
    var doc = document;
    var docNode = serializeNode(doc, doc);

    return {
      type: EventType.FullSnapshot,
      data: {
        node: docNode,
        initialOffset: {
          left: window.scrollX || window.pageXOffset || 0,
          top: window.scrollY || window.pageYOffset || 0
        }
      },
      timestamp: Date.now()
    };
  }

  // Create Incremental Snapshot event (type 3)
  function createIncrementalSnapshot(source, data) {
    return {
      type: EventType.IncrementalSnapshot,
      data: Object.assign({ source: source }, data),
      timestamp: Date.now()
    };
  }

  // Event Handlers
  var EventHandlers = {
    // Mouse move handler - throttled and batched
    mousemove: function(event) {
      var now = Date.now();

      STATE.mousePositions.push({
        x: event.clientX,
        y: event.clientY,
        id: getNodeId(event.target),
        timeOffset: now - STATE.startTime
      });

      // Flush mouse positions periodically
      if (now - STATE.lastMouseMoveTime > CONFIG.mouseMoveThrottle) {
        if (STATE.mousePositions.length > 0) {
          recordEvent(createIncrementalSnapshot(IncrementalSource.MouseMove, {
            positions: STATE.mousePositions.slice()
          }));
          STATE.mousePositions = [];
        }
        STATE.lastMouseMoveTime = now;
      }
    },

    // Click handler
    click: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.Click,
        id: getNodeId(event.target),
        x: event.clientX,
        y: event.clientY
      }));
    },

    // Mouse down
    mousedown: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.MouseDown,
        id: getNodeId(event.target),
        x: event.clientX,
        y: event.clientY
      }));
    },

    // Mouse up
    mouseup: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.MouseUp,
        id: getNodeId(event.target),
        x: event.clientX,
        y: event.clientY
      }));
    },

    // Double click
    dblclick: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.DblClick,
        id: getNodeId(event.target),
        x: event.clientX,
        y: event.clientY
      }));
    },

    // Context menu (right click)
    contextmenu: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.ContextMenu,
        id: getNodeId(event.target),
        x: event.clientX,
        y: event.clientY
      }));
    },

    // Focus
    focus: function(event) {
      var target = event.target;
      if (target && target.nodeType === Node.ELEMENT_NODE) {
        recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
          type: MouseInteractions.Focus,
          id: getNodeId(target)
        }));
      }
    },

    // Blur
    blur: function(event) {
      var target = event.target;
      if (target && target.nodeType === Node.ELEMENT_NODE) {
        recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
          type: MouseInteractions.Blur,
          id: getNodeId(target)
        }));
      }
    },

    // Touch start
    touchstart: function(event) {
      if (event.touches && event.touches.length > 0) {
        var touch = event.touches[0];
        recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
          type: MouseInteractions.TouchStart,
          id: getNodeId(event.target),
          x: touch.clientX,
          y: touch.clientY
        }));
      }
    },

    // Touch end
    touchend: function(event) {
      recordEvent(createIncrementalSnapshot(IncrementalSource.MouseInteraction, {
        type: MouseInteractions.TouchEnd,
        id: getNodeId(event.target)
      }));
    },

    // Touch move - similar to mouse move
    touchmove: function(event) {
      if (event.touches && event.touches.length > 0) {
        var touch = event.touches[0];
        var now = Date.now();

        STATE.mousePositions.push({
          x: touch.clientX,
          y: touch.clientY,
          id: getNodeId(event.target),
          timeOffset: now - STATE.startTime
        });

        if (now - STATE.lastMouseMoveTime > CONFIG.mouseMoveThrottle) {
          if (STATE.mousePositions.length > 0) {
            recordEvent(createIncrementalSnapshot(IncrementalSource.TouchMove, {
              positions: STATE.mousePositions.slice()
            }));
            STATE.mousePositions = [];
          }
          STATE.lastMouseMoveTime = now;
        }
      }
    },

    // Scroll handler
    scroll: function(event) {
      var target = event.target;
      if (target === document) {
        target = document.scrollingElement || document.documentElement;
      }

      var id = getNodeId(target);
      var scrollLeft = target.scrollLeft || 0;
      var scrollTop = target.scrollTop || 0;

      // Debounce scroll events per element
      var key = id.toString();
      var lastScroll = STATE.scrollPositions.get(key);

      if (!lastScroll || Date.now() - lastScroll.time > 100) {
        recordEvent(createIncrementalSnapshot(IncrementalSource.Scroll, {
          id: id,
          x: scrollLeft,
          y: scrollTop
        }));
        STATE.scrollPositions.set(key, { time: Date.now(), x: scrollLeft, y: scrollTop });
      }
    },

    // Resize handler
    resize: function() {
      recordEvent(createIncrementalSnapshot(IncrementalSource.ViewportResize, {
        width: window.innerWidth,
        height: window.innerHeight
      }));
    },

    // Input handler
    input: function(event) {
      var target = event.target;
      if (!target || target.nodeType !== Node.ELEMENT_NODE) return;

      var tagName = target.tagName.toLowerCase();
      if (tagName !== 'input' && tagName !== 'textarea' && tagName !== 'select') return;

      var id = getNodeId(target);
      var value = target.value || '';

      // Mask value if needed
      if (shouldMaskInput(target)) {
        value = '*'.repeat(value.length);
      }

      var inputData = {
        id: id,
        text: value
      };

      // For checkboxes and radios, record checked state
      if (target.type === 'checkbox' || target.type === 'radio') {
        inputData.isChecked = target.checked;
      }

      recordEvent(createIncrementalSnapshot(IncrementalSource.Input, inputData));
    },

    // Media play
    mediaPlay: function(event) {
      var target = event.target;
      recordEvent(createIncrementalSnapshot(IncrementalSource.MediaInteraction, {
        type: MediaInteractions.Play,
        id: getNodeId(target),
        currentTime: target.currentTime
      }));
    },

    // Media pause
    mediaPause: function(event) {
      var target = event.target;
      recordEvent(createIncrementalSnapshot(IncrementalSource.MediaInteraction, {
        type: MediaInteractions.Pause,
        id: getNodeId(target),
        currentTime: target.currentTime
      }));
    },

    // Media seeked
    mediaSeeked: function(event) {
      var target = event.target;
      recordEvent(createIncrementalSnapshot(IncrementalSource.MediaInteraction, {
        type: MediaInteractions.Seeked,
        id: getNodeId(target),
        currentTime: target.currentTime
      }));
    },

    // Media volume change
    mediaVolumeChange: function(event) {
      var target = event.target;
      recordEvent(createIncrementalSnapshot(IncrementalSource.MediaInteraction, {
        type: MediaInteractions.VolumeChange,
        id: getNodeId(target),
        volume: target.volume,
        muted: target.muted
      }));
    },

    // Media rate change
    mediaRateChange: function(event) {
      var target = event.target;
      recordEvent(createIncrementalSnapshot(IncrementalSource.MediaInteraction, {
        type: MediaInteractions.RateChange,
        id: getNodeId(target),
        playbackRate: target.playbackRate
      }));
    },

    // Before unload
    beforeunload: function() {
      // Flush any remaining mouse positions
      if (STATE.mousePositions.length > 0) {
        recordEvent(createIncrementalSnapshot(IncrementalSource.MouseMove, {
          positions: STATE.mousePositions.slice()
        }));
        STATE.mousePositions = [];
      }

      // Send final chunk
      sendChunk(true);
    },

    // Visibility change
    visibilitychange: function() {
      if (document.hidden) {
        // Page hidden - send current data
        sendChunk();
      }
    }
  };

  // Attach event listeners
  function attachEventListeners() {
    // Mouse events
    document.addEventListener('mousemove', EventHandlers.mousemove, { passive: true });
    document.addEventListener('click', EventHandlers.click, { capture: true });
    document.addEventListener('mousedown', EventHandlers.mousedown, { capture: true });
    document.addEventListener('mouseup', EventHandlers.mouseup, { capture: true });
    document.addEventListener('dblclick', EventHandlers.dblclick, { capture: true });
    document.addEventListener('contextmenu', EventHandlers.contextmenu, { capture: true });

    // Focus/blur
    document.addEventListener('focus', EventHandlers.focus, { capture: true });
    document.addEventListener('blur', EventHandlers.blur, { capture: true });

    // Touch events
    document.addEventListener('touchstart', EventHandlers.touchstart, { passive: true });
    document.addEventListener('touchend', EventHandlers.touchend, { passive: true });
    document.addEventListener('touchmove', EventHandlers.touchmove, { passive: true });

    // Scroll
    document.addEventListener('scroll', EventHandlers.scroll, { capture: true, passive: true });

    // Resize
    window.addEventListener('resize', EventHandlers.resize);

    // Input
    document.addEventListener('input', EventHandlers.input, { capture: true });

    // Before unload
    window.addEventListener('beforeunload', EventHandlers.beforeunload);

    // Visibility
    document.addEventListener('visibilitychange', EventHandlers.visibilitychange);

    // Attach media listeners
    attachMediaListeners();

    if (CONFIG.debug) {
      console.log('Session recording event listeners attached');
    }
  }

  // Remove event listeners
  function removeEventListeners() {
    document.removeEventListener('mousemove', EventHandlers.mousemove);
    document.removeEventListener('click', EventHandlers.click, { capture: true });
    document.removeEventListener('mousedown', EventHandlers.mousedown, { capture: true });
    document.removeEventListener('mouseup', EventHandlers.mouseup, { capture: true });
    document.removeEventListener('dblclick', EventHandlers.dblclick, { capture: true });
    document.removeEventListener('contextmenu', EventHandlers.contextmenu, { capture: true });

    document.removeEventListener('focus', EventHandlers.focus, { capture: true });
    document.removeEventListener('blur', EventHandlers.blur, { capture: true });

    document.removeEventListener('touchstart', EventHandlers.touchstart);
    document.removeEventListener('touchend', EventHandlers.touchend);
    document.removeEventListener('touchmove', EventHandlers.touchmove);

    document.removeEventListener('scroll', EventHandlers.scroll, { capture: true });
    window.removeEventListener('resize', EventHandlers.resize);

    document.removeEventListener('input', EventHandlers.input, { capture: true });

    window.removeEventListener('beforeunload', EventHandlers.beforeunload);
    document.removeEventListener('visibilitychange', EventHandlers.visibilitychange);

    removeMediaListeners();
  }

  // Attach media event listeners
  function attachMediaListeners() {
    var mediaElements = document.querySelectorAll('video, audio');
    mediaElements.forEach(function(element) {
      if (element._srAttached) return;

      element.addEventListener('play', EventHandlers.mediaPlay);
      element.addEventListener('pause', EventHandlers.mediaPause);
      element.addEventListener('seeked', EventHandlers.mediaSeeked);
      element.addEventListener('volumechange', EventHandlers.mediaVolumeChange);
      element.addEventListener('ratechange', EventHandlers.mediaRateChange);

      element._srAttached = true;
    });
  }

  // Remove media event listeners
  function removeMediaListeners() {
    var mediaElements = document.querySelectorAll('video, audio');
    mediaElements.forEach(function(element) {
      if (!element._srAttached) return;

      element.removeEventListener('play', EventHandlers.mediaPlay);
      element.removeEventListener('pause', EventHandlers.mediaPause);
      element.removeEventListener('seeked', EventHandlers.mediaSeeked);
      element.removeEventListener('volumechange', EventHandlers.mediaVolumeChange);
      element.removeEventListener('ratechange', EventHandlers.mediaRateChange);

      element._srAttached = false;
    });
  }

  // Start Mutation Observer
  function startMutationObserver() {
    if (!window.MutationObserver || !document.body) {
      setTimeout(startMutationObserver, 100);
      return;
    }

    STATE.observers.mutation = new MutationObserver(function(mutations) {
      var texts = [];
      var attributes = [];
      var removes = [];
      var adds = [];

      mutations.forEach(function(mutation) {
        var targetId = getNodeId(mutation.target);

        if (mutation.type === 'characterData') {
          // Text content change
          texts.push({
            id: targetId,
            value: mutation.target.textContent || ''
          });
        } else if (mutation.type === 'attributes') {
          // Attribute change
          var attrValue = mutation.target.getAttribute(mutation.attributeName);
          attributes.push({
            id: targetId,
            attributes: {
              [mutation.attributeName]: attrValue
            }
          });
        } else if (mutation.type === 'childList') {
          // Removed nodes
          mutation.removedNodes.forEach(function(node) {
            var nodeId = STATE.nodeIdMap.get(node);
            if (nodeId !== undefined) {
              removes.push({
                parentId: targetId,
                id: nodeId
              });
            }
          });

          // Added nodes
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === Node.ELEMENT_NODE || node.nodeType === Node.TEXT_NODE) {
              var serialized = serializeNode(node, document);
              if (serialized) {
                var previousSibling = node.previousSibling;
                var nextSibling = node.nextSibling;

                adds.push({
                  parentId: targetId,
                  nextId: nextSibling ? getNodeId(nextSibling) : null,
                  previousId: previousSibling ? getNodeId(previousSibling) : null,
                  node: serialized
                });

                // Attach media listeners to new media elements
                if (node.nodeType === Node.ELEMENT_NODE) {
                  if (node.tagName === 'VIDEO' || node.tagName === 'AUDIO') {
                    attachMediaListenersToElement(node);
                  }
                  var nestedMedia = node.querySelectorAll && node.querySelectorAll('video, audio');
                  if (nestedMedia) {
                    nestedMedia.forEach(attachMediaListenersToElement);
                  }
                }
              }
            }
          });
        }
      });

      // Only record if there are actual changes
      if (texts.length > 0 || attributes.length > 0 || removes.length > 0 || adds.length > 0) {
        recordEvent(createIncrementalSnapshot(IncrementalSource.Mutation, {
          texts: texts,
          attributes: attributes,
          removes: removes,
          adds: adds
        }));
      }
    });

    STATE.observers.mutation.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
      attributeOldValue: false,
      characterDataOldValue: false
    });

    if (CONFIG.debug) {
      console.log('Mutation observer started');
    }
  }

  // Attach media listeners to a single element
  function attachMediaListenersToElement(element) {
    if (element._srAttached) return;

    element.addEventListener('play', EventHandlers.mediaPlay);
    element.addEventListener('pause', EventHandlers.mediaPause);
    element.addEventListener('seeked', EventHandlers.mediaSeeked);
    element.addEventListener('volumechange', EventHandlers.mediaVolumeChange);
    element.addEventListener('ratechange', EventHandlers.mediaRateChange);

    element._srAttached = true;
  }

  // Stop Mutation Observer
  function stopMutationObserver() {
    if (STATE.observers.mutation) {
      STATE.observers.mutation.disconnect();
      STATE.observers.mutation = null;
    }
  }

  // Send chunk to API - Optimized with event restoration on failure
  function sendChunk(isFinal) {
    // Safety check: don't send if unmounted (except for final send)
    if (!STATE.isMounted && !isFinal) return;

    // Prevent concurrent sends
    if (STATE.pendingSend && !isFinal) return;

    if (isConsentMissing()) {
      if (CONFIG.debug) console.warn('⚠️ Consent missing, cannot send session data');
      return;
    }

    if (!CONFIG.domainId) {
      if (CONFIG.debug) console.warn('⚠️ DomainId not configured');
      return;
    }

    if (STATE.events.length === 0 && !isFinal) {
      if (CONFIG.debug) console.log('ℹ️ No events to send');
      return;
    }

    STATE.chunkNumber++;
    STATE.pendingSend = true;

    var now = Date.now();

    // Copy events to send and clear buffer immediately (aligned with OptimizedSessionRecorder.tsx)
    var eventsToSend = STATE.events.slice();
    STATE.events = [];  // Clear buffer immediately for new events

    // Always prepend Meta event at start of each chunk for rrweb playback
    // This ensures the player knows the page URL and viewport dimensions
    var metaEvent = createMetaEvent();
    eventsToSend.unshift(metaEvent);

    // Build chunk data structure (aligned with OptimizedSessionRecorder.tsx)
    var chunkData = {
      sessionId: STATE.sessionId,
      chunkNumber: STATE.chunkNumber,
      timestamp: now,
      events: eventsToSend,
      totalEvents: eventsToSend.length,
      chunkType: 'minute_split',
      metadata: {
        url: window.location.href,
        userAgent: navigator.userAgent,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight
        },
        recordedAt: new Date().toISOString(),
        startTime: STATE.startTime,
        duration: now - STATE.startTime
      }
    };

    // Session_Recording object structure
    var sessionRecordingData = {
      name: 'Session_Recording',
      status: 'success',
      data: chunkData,
      lastUpdate: now,
      count: STATE.chunkNumber
    };

    // Full payload structure
    var payload = {
      domainId: CONFIG.domainId,
      domainName: CONFIG.domainName,
      visitorId: STATE.visitorId,
      type: 'sessionrecord',
      trackingData: {
        Session_Recording: sessionRecordingData
      }
    };

    // Send data with error handling and event restoration
    var sendData = function() {
      try {
        if (isFinal && navigator.sendBeacon) {
          // Use sendBeacon for final send (works during page unload)
          var success = navigator.sendBeacon(CONFIG.apiEndpoint, JSON.stringify(payload));
          if (!success && CONFIG.debug) {
            console.warn('⚠️ sendBeacon failed, data may be lost');
          }
          STATE.pendingSend = false;
        } else {
          fetch(CONFIG.apiEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            keepalive: true
          })
          .then(function(response) {
            STATE.pendingSend = false;
            if (!response.ok) {
              throw new Error('HTTP ' + response.status);
            }
            if (CONFIG.debug) {
              console.log('✅ Session chunk sent:', STATE.chunkNumber, 'events:', eventsToSend.length);
            }
          })
          .catch(function(error) {
            STATE.pendingSend = false;
            if (CONFIG.debug) {
              console.error('❌ Failed to send session recording:', error);
            }
            // Restore events on failure (aligned with OptimizedSessionRecorder.tsx)
            // Prepend failed events back to buffer so they're not lost
            if (STATE.isMounted) {
              STATE.events = eventsToSend.slice(1).concat(STATE.events); // Skip the prepended meta event
              if (CONFIG.debug) {
                console.log('🔄 Events restored to buffer:', eventsToSend.length - 1);
              }
            }
          });
        }
      } catch (error) {
        STATE.pendingSend = false;
        if (CONFIG.debug) {
          console.error('❌ Error sending session data:', error);
        }
        // Restore events on error
        if (STATE.isMounted) {
          STATE.events = eventsToSend.slice(1).concat(STATE.events);
        }
      }
    };

    sendData();
  }

  // Start chunk sending timer - Optimized with isMounted check
  function startChunkTimer() {
    if (STATE.chunkTimer) clearInterval(STATE.chunkTimer);

    // Set up interval to send data every 60 seconds (aligned with OptimizedSessionRecorder.tsx)
    STATE.chunkTimer = setInterval(function() {
      // Only send if still mounted and recording
      if (STATE.isMounted && STATE.isRecording) {
        sendChunk();
      }
    }, CONFIG.chunkInterval);

    if (CONFIG.debug) {
      console.log('⏱️ Chunk timer started:', CONFIG.chunkInterval, 'ms');
    }
  }

  // Stop chunk timer
  function stopChunkTimer() {
    if (STATE.chunkTimer) {
      clearInterval(STATE.chunkTimer);
      STATE.chunkTimer = null;
    }
  }

  // Start recording - Optimized initialization (aligned with OptimizedSessionRecorder.tsx)
  function startRecording(options) {
    if (STATE.isRecording) {
      if (CONFIG.debug) console.warn('⚠️ Session recording already started');
      return;
    }

    if (isConsentMissing()) {
      if (CONFIG.debug) console.warn('⚠️ Consent missing, session recording disabled');
      return;
    }

    // Merge options
    if (options) {
      Object.assign(CONFIG, options);
    }

    // Initialize IDs
    initVisitorId();
    initSessionId();

    // Reset state for clean start (aligned with OptimizedSessionRecorder.tsx)
    STATE.isMounted = true;
    STATE.isRecording = true;
    STATE.pendingSend = false;
    STATE.startTime = Date.now();
    STATE.events = [];
    STATE.chunkNumber = 0;
    STATE.nodeIdCounter = 1;
    STATE.nodeIdMap = new WeakMap();
    STATE.idNodeMap = new Map();
    STATE.mousePositions = [];
    STATE.scrollPositions = new Map();

    if (CONFIG.debug) {
      console.log('🎥 Starting optimized session recording:', STATE.sessionId);
    }

    try {
      // Record initial events
      recordEvent(createMetaEvent());
      recordEvent(createFullSnapshot());

      // Start observers and listeners
      attachEventListeners();
      startMutationObserver();
      startChunkTimer();

      if (CONFIG.debug) {
        console.log('✅ Session recording started', {
          sessionId: STATE.sessionId,
          visitorId: STATE.visitorId
        });
      }
    } catch (error) {
      if (CONFIG.debug) {
        console.error('❌ Failed to start recording:', error);
      }
      STATE.isRecording = false;
      STATE.isMounted = false;
    }
  }

  // Stop recording - Optimized cleanup (aligned with OptimizedSessionRecorder.tsx)
  function stopRecording() {
    if (!STATE.isRecording) return;

    // Set isMounted to false first (aligned with OptimizedSessionRecorder.tsx)
    STATE.isMounted = false;
    STATE.isRecording = false;

    // Send remaining events before cleanup
    if (STATE.events.length > 0) {
      sendChunk(true);
    }

    // Stop recording observers first
    stopMutationObserver();

    // Clear interval
    stopChunkTimer();

    // Remove event listeners
    removeEventListeners();

    if (CONFIG.debug) {
      console.log('🛑 Session recording stopped');
    }
  }

  // Pause recording - with isMounted safety check
  function pauseRecording() {
    if (!STATE.isRecording || !STATE.isMounted) return;

    removeEventListeners();
    stopMutationObserver();
    stopChunkTimer();

    // Add custom pause event
    recordEvent({
      type: EventType.Custom,
      data: {
        tag: 'session_pause',
        payload: { timestamp: Date.now() }
      },
      timestamp: Date.now()
    });

    if (CONFIG.debug) {
      console.log('⏸️ Session recording paused');
    }
  }

  // Resume recording - with isMounted safety check
  function resumeRecording() {
    if (!STATE.isRecording || !STATE.isMounted) return;

    // Add custom resume event
    recordEvent({
      type: EventType.Custom,
      data: {
        tag: 'session_resume',
        payload: { timestamp: Date.now() }
      },
      timestamp: Date.now()
    });

    attachEventListeners();
    startMutationObserver();
    startChunkTimer();

    if (CONFIG.debug) {
      console.log('▶️ Session recording resumed');
    }
  }

  // Take a new full snapshot (useful after major DOM changes)
  function takeFullSnapshot() {
    if (!STATE.isRecording || !STATE.isMounted) return;

    recordEvent(createMetaEvent());
    recordEvent(createFullSnapshot());

    if (CONFIG.debug) {
      console.log('📸 Full snapshot taken');
    }
  }

  // Public API
  window.SessionRecording = {
    start: function(options) {
      startRecording(options);
    },

    stop: function() {
      stopRecording();
    },

    pause: function() {
      pauseRecording();
    },

    resume: function() {
      resumeRecording();
    },

    sendNow: function() {
      sendChunk();
    },

    takeSnapshot: function() {
      takeFullSnapshot();
    },

    getSessionData: function() {
      return {
        sessionId: STATE.sessionId,
        visitorId: STATE.visitorId,
        events: STATE.events,
        chunkNumber: STATE.chunkNumber,
        duration: STATE.isRecording ? Date.now() - STATE.startTime : 0,
        isRecording: STATE.isRecording,
        eventCount: STATE.events.length
      };
    },

    getSessionId: function() {
      return STATE.sessionId;
    },

    getVisitorId: function() {
      return STATE.visitorId;
    },

    isRecording: function() {
      return STATE.isRecording && STATE.isMounted;
    },

    isMounted: function() {
      return STATE.isMounted;
    },

    configure: function(options) {
      Object.assign(CONFIG, options);
    },

    // Add custom event - with safety checks
    addCustomEvent: function(tag, payload) {
      if (!STATE.isRecording || !STATE.isMounted) return;

      recordEvent({
        type: EventType.Custom,
        data: {
          tag: tag,
          payload: payload
        },
        timestamp: Date.now()
      });
    }
  };

  // Auto-initialize
  function init() {
    if (window.SessionRecordingConfig) {
      if (window.SessionRecordingConfig.autoStart !== false) {
        startRecording(window.SessionRecordingConfig);
      }
    } else if (CONFIG.autoStart) {
      startRecording();
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})(window, document);
